import type { NativeMobilePlatform, PlatformInfo } from "./types"

export type MobileLiveStatusPolicyReason =
    | "not-loaded"
    | "policy-unavailable"
    | "policy-expired"
    | "enabled"
    | "platform-disabled"
    | "app-version-required"
    | "invalid-app-version"
    | "version-below-minimum"
    | "version-blocked"
    | "app-build-required"
    | "invalid-app-build"
    | "build-below-minimum"

export interface MobileLiveStatusPolicy {
    state: "unknown" | "enabled" | "disabled" | "unavailable"
    liveStatusEnabled: boolean
    reason: MobileLiveStatusPolicyReason
    platform: NativeMobilePlatform | null
    appVersion: string | null
    appBuild: string | null
    expiresAt: string | null
}

interface MobilePolicyResponse {
    platform: NativeMobilePlatform
    appVersion: string | null
    appBuild: string | null
    liveStatusEnabled: boolean
    reason: Exclude<MobileLiveStatusPolicyReason, "not-loaded" | "policy-unavailable" | "policy-expired">
    expiresAt: string
}

const decisionReasons = new Set<string>([
    "enabled",
    "platform-disabled",
    "app-version-required",
    "invalid-app-version",
    "version-below-minimum",
    "version-blocked",
    "app-build-required",
    "invalid-app-build",
    "build-below-minimum",
])

let currentPolicy: MobileLiveStatusPolicy = unavailablePolicy("unknown")

export function getMobileLiveStatusPolicy(now: Date = new Date()): MobileLiveStatusPolicy {
    if (currentPolicy.expiresAt && Date.parse(currentPolicy.expiresAt) <= now.getTime()) {
        currentPolicy = { ...currentPolicy, state: "unavailable", liveStatusEnabled: false, reason: "policy-expired" }
    }
    return currentPolicy
}

export function applyMobileLiveStatusPolicy(value: unknown, expected: PlatformInfo, now: Date = new Date()): MobileLiveStatusPolicy {
    if (expected.kind !== "ios" && expected.kind !== "android") throw new Error("Mobile policy requires a native mobile platform")
    const response = parseMobilePolicyResponse(value)
    if (response.platform !== expected.kind) throw new Error("Mobile policy platform does not match this app")
    if ((expected.appVersion ?? null) !== response.appVersion || (expected.appBuild ?? null) !== response.appBuild) {
        throw new Error("Mobile policy release does not match this app")
    }
    if (Date.parse(response.expiresAt) <= now.getTime()) throw new Error("Mobile policy response has expired")
    currentPolicy = {
        state: response.liveStatusEnabled ? "enabled" : "disabled",
        liveStatusEnabled: response.liveStatusEnabled,
        reason: response.reason,
        platform: response.platform,
        appVersion: response.appVersion,
        appBuild: response.appBuild,
        expiresAt: response.expiresAt,
    }
    return currentPolicy
}

export function markMobileLiveStatusPolicyUnavailable(): MobileLiveStatusPolicy {
    currentPolicy = unavailablePolicy("unavailable")
    return currentPolicy
}

export function resetMobileLiveStatusPolicyForTests(): void {
    currentPolicy = unavailablePolicy("unknown")
}

function parseMobilePolicyResponse(value: unknown): MobilePolicyResponse {
    const source = record(value, "mobile policy response")
    if (source.version !== 1) throw new Error("Mobile policy version is invalid")
    const platform = source.platform
    if (platform !== "ios" && platform !== "android") throw new Error("Mobile policy platform is invalid")
    const reason = source.reason
    if (typeof reason !== "string" || !isDecisionReason(reason)) {
        throw new Error("Mobile policy reason is invalid")
    }
    if (typeof source.liveStatusEnabled !== "boolean" || source.liveStatusEnabled !== (reason === "enabled")) {
        throw new Error("Mobile policy decision is inconsistent")
    }
    return {
        platform,
        appVersion: optionalReleaseValue(source.appVersion, "appVersion"),
        appBuild: optionalReleaseValue(source.appBuild, "appBuild"),
        liveStatusEnabled: source.liveStatusEnabled,
        reason,
        expiresAt: isoTimestamp(source.expiresAt, "expiresAt"),
    }
}

function isDecisionReason(value: string): value is MobilePolicyResponse["reason"] {
    return decisionReasons.has(value)
}

function unavailablePolicy(state: "unknown" | "unavailable"): MobileLiveStatusPolicy {
    return {
        state,
        liveStatusEnabled: false,
        reason: state === "unknown" ? "not-loaded" : "policy-unavailable",
        platform: null,
        appVersion: null,
        appBuild: null,
        expiresAt: null,
    }
}

function record(value: unknown, label: string): Record<string, unknown> {
    if (!isRecord(value)) throw new Error(`${label} is invalid`)
    return value
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value)
}

function optionalReleaseValue(value: unknown, field: string): string | null {
    if (value === undefined) return null
    if (typeof value !== "string" || value.length === 0 || value.length > 64) throw new Error(`Mobile policy ${field} is invalid`)
    return value
}

function isoTimestamp(value: unknown, field: string): string {
    if (typeof value !== "string" || !Number.isFinite(Date.parse(value))) throw new Error(`Mobile policy ${field} is invalid`)
    return value
}
